import type { SpellWorkerRequest, SpellWorkerResponse } from './spell-check-protocol';

/** What the spell-check extension needs from a dictionary engine. Tests supply a fake one. */
export interface SpellCheckEngine {
  /** Returns the subset of `words` that are misspelled. */
  check(words: readonly string[]): Promise<ReadonlySet<string>>;
  suggest(word: string): Promise<string[]>;
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
type Pending = { resolve: (response: SpellWorkerResponse) => void; reject: (error: Error) => void };

export const SPELL_REQUEST_TIMEOUT_MS = 30_000;

class WorkerSpellCheckEngine implements SpellCheckEngine {
  private worker: Worker | null = null;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const worker = new Worker(new URL('./spell-check.worker.ts', import.meta.url), { type: 'module', name: 'spell-check' });
    worker.onmessage = ({ data }: MessageEvent<SpellWorkerResponse>) => {
      if (!data || typeof data !== 'object' || typeof data.id !== 'number') return;
      const request = this.pending.get(data.id);
      if (!request) return;
      this.pending.delete(data.id);
      if (data.type === 'error') request.reject(new Error(data.message));
      else request.resolve(data);
    };
    worker.onerror = () => this.failAll('Spell checking is unavailable.');
    this.worker = worker;
    return worker;
  }

  private failAll(message: string) {
    for (const request of this.pending.values()) request.reject(new Error(message));
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
  }

  private request(message: DistributiveOmit<SpellWorkerRequest, 'id'>): Promise<SpellWorkerResponse> {
    const worker = this.ensureWorker();
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) reject(new Error('Spell checking took too long.'));
      }, SPELL_REQUEST_TIMEOUT_MS);
      this.pending.set(id, {
        resolve: response => { clearTimeout(timer); resolve(response); },
        reject: error => { clearTimeout(timer); reject(error); },
      });
      worker.postMessage({ ...message, id } as SpellWorkerRequest);
    });
  }

  async check(words: readonly string[]): Promise<ReadonlySet<string>> {
    if (!words.length) return new Set();
    const response = await this.request({ type: 'check', words: [...words] });
    return new Set(response.type === 'checked' ? response.misspelled : []);
  }

  async suggest(word: string): Promise<string[]> {
    const response = await this.request({ type: 'suggest', word });
    return response.type === 'suggestions' ? response.suggestions : [];
  }
}

let sharedEngine: SpellCheckEngine | null = null;

/** One worker for every editor on the page; it is created only when spelling is first needed. */
export function getBrowserSpellCheckEngine(): SpellCheckEngine | null {
  if (typeof Worker === 'undefined') return null;
  sharedEngine ??= new WorkerSpellCheckEngine();
  return sharedEngine;
}
